import React from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import Barcode from 'react-barcode';
import backstockSimulatorService from '../../../services/backstockSimulatorService';
import gamesService from '../../../services/gamesService';
import type { Game, ScannerConfig } from '../../../types/features/games';
import type { LotteryPackSnapshot } from '../../../types/services/lotteryPacks';
import { translateBarcodeFormat } from '../../../utils/barcodeFormatTranslator';
import { buildTicketBarcode, buildTicketDisplay } from '../../../utils/ticketFactory';
import { getGameImageSource } from '../../../utils/normalizers/gameImageUrl';
import '../../../components/lottery/TicketCard/TicketCard.css';
import { adminColors, adminTypography } from '../../../styles/adminDesignTokens';
import {
  adminButtonSx,
  adminButtonToneSx,
  adminCombineSx,
} from '../../../styles/adminComponentStyles';
import {
  getBackstockSimulatorTicketNumber,
  isBackstockInventoryPack,
  isFullBackstockPack,
} from '../utils/backstockSimulatorPacks';

type BackstockSimulatorContentProps = {
  stateCode?: string | null;
  storeId: string;
};

type BackstockPackCardProps = {
  game?: Game;
  pack: LotteryPackSnapshot;
  scannerConfig: ScannerConfig | null;
};

const formatLastSeen = (value: string | null): string => {
  if (!value) {
    return 'Never audited';
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Unknown' : date.toLocaleString();
};

const BackstockPackCard: React.FC<BackstockPackCardProps> = React.memo(({
  game,
  pack,
  scannerConfig,
}) => {
  const ticketNumber = getBackstockSimulatorTicketNumber(pack);
  const isFullPack = isFullBackstockPack(pack);
  const barcode = React.useMemo(() => {
    if (!scannerConfig?.barcode_format || ticketNumber === null) {
      return null;
    }

    try {
      return {
        display: buildTicketDisplay({
          format: scannerConfig.barcode_format,
          gameNumber: pack.game_id,
          packId: pack.pack_id,
          randomDigit: '0',
          ticketNumber,
        }),
        value: buildTicketBarcode({
          format: scannerConfig.barcode_format,
          gameNumber: pack.game_id,
          packId: pack.pack_id,
          ticketNumber,
        }),
      };
    } catch {
      return null;
    }
  }, [pack.game_id, pack.pack_id, scannerConfig?.barcode_format, ticketNumber]);
  const barcodeFormat = translateBarcodeFormat(scannerConfig?.barcode_scanner_formats?.[0]);
  const imageUrl = game ? getGameImageSource(game) : '';

  return (
    <Card elevation={8} className="TicketCard">
      <CardContent className="content" sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box className="top">
          {imageUrl ? (
            <CardMedia
              component="img"
              image={imageUrl}
              alt={pack.game_name || `Game ${pack.game_id}`}
              className="media"
            />
          ) : (
            <Box
              className="media"
              sx={{ alignItems: 'center', bgcolor: '#f7f9fc', display: 'flex', justifyContent: 'center' }}
            >
              <Typography sx={{ ...adminTypography.helper, color: adminColors.mutedText }}>
                No image
              </Typography>
            </Box>
          )}

          <Box className="info">
            <Box className="header-row">
              <Box>
                <Typography variant="body1" className="title">
                  {pack.game_name || `Game ${pack.game_id}`}
                </Typography>
                <Typography variant="body2" className="subtitle">
                  Game {pack.game_id}
                </Typography>
              </Box>
              <Chip
                size="small"
                color={isFullPack ? 'success' : 'warning'}
                label={isFullPack ? 'Full pack' : 'Partial pack'}
              />
            </Box>
            <hr />

            <Box className="meta">
              <Box className="metaRow">
                <Typography variant="body2" className="metaLabel">Pack:</Typography>
                <Typography variant="body2" className="metaValue">
                  {pack.display_pack_id || pack.pack_id}
                </Typography>
              </Box>
              <Box className="metaRow">
                <Typography variant="body2" className="metaLabel">Tickets Remaining:</Typography>
                <Typography variant="body2" className="metaValue">
                  {pack.tickets_remaining ?? '-'} / {pack.tickets_per_pack ?? '-'}
                </Typography>
              </Box>
              <Box className="metaRow">
                <Typography variant="body2" className="metaLabel">Simulated Ticket:</Typography>
                <Typography variant="body2" className="metaValue">{ticketNumber ?? '-'}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          className="bottom"
          sx={{
            alignItems: 'flex-start',
            display: 'flex',
            justifyContent: 'space-between',
            minHeight: 118,
            width: '100%',
          }}
        >
          <Box className="barcode" sx={{ flex: '0 0 58%', maxWidth: '58%' }}>
            <Box className="barcodeInner" sx={{ height: 66, overflow: 'hidden' }}>
              {barcode && barcodeFormat ? (
                <Barcode
                  value={barcode.value}
                  format={barcodeFormat}
                  width={1.25}
                  height={58}
                  displayValue={false}
                  margin={0}
                />
              ) : (
                <Typography sx={{ ...adminTypography.helper, color: adminColors.mutedText }}>
                  Barcode format unavailable
                </Typography>
              )}
            </Box>
            {barcode && (
              <>
                <Typography className="number" sx={{ whiteSpace: 'nowrap' }}>
                  {barcode.display}
                </Typography>
                <Typography className="barcodeMeta" sx={{ whiteSpace: 'nowrap' }}>
                  {scannerConfig?.barcode_scanner_formats?.[0] || 'Barcode'} · {scannerConfig?.barcode_length || barcode.value.length} digits
                </Typography>
              </>
            )}
          </Box>

          <Box
            className="actions"
            sx={{
              alignItems: 'flex-start',
              flex: '0 0 42%',
              gap: 0.5,
              maxWidth: '42%',
              minWidth: 0,
              pl: 1.5,
            }}
          >
            <Typography sx={{ ...adminTypography.label, color: adminColors.textSoft, whiteSpace: 'nowrap' }}>
              Last seen
            </Typography>
            <Typography
              title={formatLastSeen(pack.backstock_last_seen_at)}
              sx={{ ...adminTypography.helper, color: adminColors.text, fontWeight: 600, whiteSpace: 'nowrap' }}
            >
              {formatLastSeen(pack.backstock_last_seen_at)}
            </Typography>
            <Typography
              title={pack.backstock_last_seen_by_username || 'Unknown user'}
              sx={{ ...adminTypography.helper, color: adminColors.mutedText, whiteSpace: 'nowrap' }}
            >
              {pack.backstock_last_seen_by_username || 'Unknown user'}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
});

const BackstockSimulatorContent: React.FC<BackstockSimulatorContentProps> = ({
  stateCode,
  storeId,
}) => {
  const [packs, setPacks] = React.useState<LotteryPackSnapshot[]>([]);
  const [games, setGames] = React.useState<Game[]>([]);
  const [scannerConfig, setScannerConfig] = React.useState<ScannerConfig | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [reloadKey, setReloadKey] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;

    const loadBackstock = async () => {
      setLoading(true);
      setError(null);

      try {
        const [packResponse, gameResponse] = await Promise.all([
          backstockSimulatorService.getLotteryPacks(storeId),
          gamesService.getGames(stateCode || ''),
        ]);
        if (cancelled) {
          return;
        }

        setPacks(packResponse.packs.filter(isBackstockInventoryPack));
        setGames(Array.isArray(gameResponse.data) ? gameResponse.data : []);
        setScannerConfig(gameResponse.scanner_config ?? null);
      } catch (loadError) {
        console.error('Error loading backstock simulator:', loadError);
        if (!cancelled) {
          setError('Failed to load backstock inventory. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadBackstock();
    return () => {
      cancelled = true;
    };
  }, [reloadKey, stateCode, storeId]);

  const handleRefresh = React.useCallback(() => {
    setReloadKey((current) => current + 1);
  }, []);
  const gamesById = React.useMemo(() => new Map(
    games.map((game) => [String(game.game_id).replace(/^0+/, ''), game]),
  ), [games]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography sx={{ ...adminTypography.pageTitle, color: adminColors.text }}>
            Backstock Simulator
          </Typography>
          <Typography sx={{ ...adminTypography.body, color: adminColors.mutedText }}>
            {packs.length} full and partial backstock {packs.length === 1 ? 'pack' : 'packs'}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          sx={adminCombineSx(adminButtonSx, adminButtonToneSx.secondary)}
        >
          Refresh
        </Button>
      </Stack>

      {error ? (
        <Alert severity="error" action={(
          <Button color="inherit" size="small" onClick={handleRefresh}>Retry</Button>
        )}>
          {error}
        </Alert>
      ) : packs.length === 0 ? (
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Typography sx={{ ...adminTypography.sectionTitle, color: adminColors.text }}>
            No backstock inventory
          </Typography>
          <Typography sx={{ ...adminTypography.body, color: adminColors.mutedText, mt: 0.5 }}>
            Full and partial packs assigned to backstock will appear here.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {packs.map((pack) => (
            <Grid size={{ xs: 12, md: 6, xl: 4 }} key={`${pack.game_id}-${pack.pack_id}`}>
              <BackstockPackCard
                game={gamesById.get(String(pack.game_id).replace(/^0+/, ''))}
                pack={pack}
                scannerConfig={scannerConfig}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default BackstockSimulatorContent;
